export type FitOptions = {
	pageWidth: number;
	pageHeight: number;
	margin?: number;
	/**
	 * Box to fit instead of the measured element bounds, e.g. the imported
	 * document's own page so its layout and whitespace are kept. Ignored when it
	 * has no positive size.
	 */
	bounds?: Bounds | null;
	/** Upper limit on the scale, e.g. 1 to only ever shrink. */
	maxScale?: number;
};

export type Bounds = {
	x: number;
	y: number;
	width: number;
	height: number;
};

const isNum = (value: unknown): value is number =>
	typeof value === 'number' && Number.isFinite(value);

const isPositive = (value: unknown): value is number => isNum(value) && value > 0;

const hasPositiveSize = (box: Bounds | null | undefined): box is Bounds =>
	!!box && isNum(box.x) && isNum(box.y) && isPositive(box.width) && isPositive(box.height);

/**
 * Bounding box over the leaf elements that carry a numeric position and size.
 * Polotno groups have no geometry of their own (only `children`, which hold
 * absolute page coordinates), so groups are recursed into and contribute
 * nothing themselves. Returns null when no leaf has geometry.
 */
export const getElementsBounds = (elements: any[]): Bounds | null => {
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;

	const visit = (list: any[]) => {
		for (const el of Array.isArray(list) ? list : []) {
			if (!el || typeof el !== 'object') continue;

			if (Array.isArray(el.children)) {
				visit(el.children);
				continue;
			}

			if (!isNum(el.x) || !isNum(el.y) || !isNum(el.width) || !isNum(el.height)) {
				continue;
			}

			minX = Math.min(minX, el.x);
			minY = Math.min(minY, el.y);
			maxX = Math.max(maxX, el.x + el.width);
			maxY = Math.max(maxY, el.y + el.height);
		}
	};

	visit(elements);

	if (minX === Infinity) return null;

	return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

// Applies the same affine (scale, then translate) to an element and every
// descendant. Group children carry absolute page coordinates.
const transform = (el: any, scale: number, dx: number, dy: number): any => {
	if (!el || typeof el !== 'object') return el;

	const next = { ...el };

	if (isNum(el.x)) next.x = el.x * scale + dx;
	if (isNum(el.y)) next.y = el.y * scale + dy;
	if (isNum(el.width)) next.width = el.width * scale;
	if (isNum(el.height)) next.height = el.height * scale;
	if (isNum(el.fontSize)) next.fontSize = el.fontSize * scale;
	if (isNum(el.strokeWidth)) next.strokeWidth = el.strokeWidth * scale;

	if (Array.isArray(el.children)) {
		next.children = el.children.map((child: any) => transform(child, scale, dx, dy));
	}

	return next;
};

/**
 * Uniformly scales and centers `elements` on a page. Pure: returns new
 * elements, never mutates the input. Rotation is left untouched.
 *
 * The box being fitted is `bounds` when it has a positive size, else the
 * measured element bounds. Returns copies unchanged when the page size is not
 * finite and positive, or when the margin leaves no room.
 */
export const fitElementsToPage = (
	elements: any[],
	{ pageWidth, pageHeight, margin = 0, bounds: sourceBounds, maxScale }: FitOptions,
): any[] => {
	const list = Array.isArray(elements) ? elements : [];
	const copies = () => list.map((el) => ({ ...el }));

	if (!isPositive(pageWidth) || !isPositive(pageHeight)) return copies();

	const inset = isNum(margin) && margin > 0 ? margin : 0;
	const availW = pageWidth - 2 * inset;
	const availH = pageHeight - 2 * inset;

	if (availW <= 0 || availH <= 0) return copies();

	const bounds = hasPositiveSize(sourceBounds) ? sourceBounds : getElementsBounds(list);

	if (!bounds) return copies();

	let scale =
		bounds.width > 0 && bounds.height > 0
			? Math.min(availW / bounds.width, availH / bounds.height)
			: 1;

	if (isPositive(maxScale)) scale = Math.min(scale, maxScale);

	const dx = (pageWidth - bounds.width * scale) / 2 - bounds.x * scale;
	const dy = (pageHeight - bounds.height * scale) / 2 - bounds.y * scale;

	return list.map((el) => transform(el, scale, dx, dy));
};
